/**
 * The in-game interface's own sounds (sfx/api.ts `uiSfx`), attached once by watching the page
 * rather than by calls scattered through the windows:
 *
 * - a paper window shown or hidden (`hidden` on its root) → open / close; the NPC dialog only
 *   closes here, since its opening already sounds from the `dialog` event (sfx/events.ts);
 * - the เมนู (Esc) menu → menuOpen / menuClose;
 * - a tab (`role="tab"`) → tab; any other button → click; any button, the hotbar's too, → a
 *   throttled hover;
 * - a press on a disabled button → error (refusals the world reports sound from `notice`);
 * - a chat line from another player (world or school channel) → chat;
 * - a press that spent jade (the jade shop, the season pass) → jade.
 *
 * One press can do several of these (a menu item closes the menu and opens a window): only the
 * weightiest plays. The hotbar's keys are left alone, since their moves sound on their own, and so
 * is any button marked `data-sfx="none"` (equip and forge, which sound from their events).
 */
import type { World } from '@nine/shared';
import { uiSfx } from '../sfx';

type Cue = 'open' | 'close' | 'menuOpen' | 'menuClose' | 'tab' | 'error' | 'jade' | 'click';

/** Which cue wins when one press makes several. */
const RANK: Readonly<Record<Cue, number>> = {
  jade: 7,
  error: 6,
  open: 5,
  close: 4,
  menuOpen: 3,
  menuClose: 2,
  tab: 1,
  click: 0,
};

/** Window roots and the cues their showing / hiding plays (null: silent that way). */
const WINDOWS: Readonly<Record<string, readonly [Cue | null, Cue | null]>> = {
  '#panel': ['open', 'close'],
  '#dialog': [null, 'close'],
  '#gameMenu': ['menuOpen', 'menuClose'],
  '#tradesMenu': ['open', 'close'],
  '#emoteMenu': ['open', 'close'],
  '#bigmap': ['open', 'close'],
  '#settings': ['open', 'close'],
  '#checkout': ['open', 'close'],
};

/** Screens with their own sounds (sfx/front.ts) or none (the cutscene). */
const ELSEWHERE = '#front, #splash, #cutscene, #loading';
const BUTTON = 'button, [role="button"], [role="tab"]';
/** A window sound this recent swallows the click of the press that caused it. */
const CLICK_AFTER_WINDOW_MS = 250;

/** The target's in-game button that should sound (pressing: not the hotbar's keys), if any. */
function buttonOf(ev: Event, pressing = true): HTMLElement | null {
  const t = ev.target;
  if (!(t instanceof Element)) return null;
  const b = t.closest<HTMLElement>(BUTTON);
  if (!b || b.closest(ELSEWHERE)) return null;
  if (pressing && (b.closest('#hotbar') || b.dataset.sfx === 'none')) return null;
  return b;
}

const disabled = (b: HTMLElement) => (b as HTMLButtonElement).disabled || b.ariaDisabled === 'true';

let attached = false;

/** Starts the interface sounds; call once the HUD and its windows are built. */
export function attachUiSounds(world: World): void {
  if (attached || typeof document === 'undefined') return;
  attached = true;

  let pending: Cue | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let windowAt = -Infinity;
  /** Jade held when the current press began (to tell a jade purchase). */
  let jadeAt: number | null = null;

  const flush = () => {
    timer = null;
    let c = pending;
    pending = null;
    if (jadeAt !== null && world.progress.jade < jadeAt) c = 'jade';
    jadeAt = null;
    if (!c) return;
    const now = performance.now();
    if (c === 'click' && now - windowAt < CLICK_AFTER_WINDOW_MS) return;
    if (c !== 'click' && c !== 'tab') windowAt = now;
    uiSfx[c]();
  };
  const cue = (c: Cue) => {
    if (!pending || RANK[c] > RANK[pending]) pending = c;
    timer ??= setTimeout(flush, 0);
  };

  // Windows shown and hidden, whoever did it (a button, a key, Esc, the world).
  const roots = new Map<Element, readonly [Cue | null, Cue | null]>();
  const watch = new MutationObserver((records) => {
    for (const r of records) {
      const el = r.target as HTMLElement;
      const was = r.oldValue !== null;
      if (was === el.hidden) continue;
      const c = roots.get(el)?.[el.hidden ? 1 : 0];
      if (c) cue(c);
    }
  });
  for (const [sel, cues] of Object.entries(WINDOWS)) {
    const el = document.querySelector(sel);
    if (!el) continue;
    roots.set(el, cues);
    watch.observe(el, { attributes: true, attributeFilter: ['hidden'], attributeOldValue: true });
  }

  // Presses: the jade before them, an error on a disabled button, then click or tab.
  document.addEventListener(
    'pointerdown',
    (ev) => {
      const b = buttonOf(ev);
      if (b && disabled(b)) cue('error');
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    'click',
    (ev) => {
      const b = buttonOf(ev);
      if (!b || disabled(b)) return;
      jadeAt = world.progress.jade;
      cue(b.getAttribute('role') === 'tab' ? 'tab' : 'click');
    },
    { capture: true, passive: true },
  );
  let over: Element | null = null;
  document.addEventListener(
    'pointerover',
    (ev) => {
      const b = buttonOf(ev, false);
      if (b && b !== over && !disabled(b)) uiSfx.hover();
      over = b;
    },
    { passive: true },
  );

  // Chat lines from other people (world and school channels; system and fight lines stay quiet).
  const chat = document.getElementById('chat');
  if (chat)
    new MutationObserver((records) => {
      const said = records.some((r) =>
        [...r.addedNodes].some(
          (n) =>
            n instanceof HTMLElement && (n.classList.contains('c-world') || n.classList.contains('c-guild')),
        ),
      );
      if (said) uiSfx.chat();
    }).observe(chat, { childList: true });
}
